// json-editor.component.ts

import { Component, Input, inject, output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl,FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-json-editor',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  //template: ``,
  templateUrl: './json-editor.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .json-editor-container {
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 10px;
      background: #fff;
      transition: all 0.3s ease;
    }

    .dark-theme {
      background: #1e1e1e;
      border-color: #444;
      color: #fff;
    }

    .toolbar {
      margin-bottom: 10px;
      display: flex;
      gap: 8px;
    }

    .btn {
      padding: 6px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .dark-theme .btn {
      background: #2d2d2d;
      border-color: #555;
      color: #fff;
    }

    .btn:hover {
      background: #f5f5f5;
    }

    .dark-theme .btn:hover {
      background: #3d3d3d;
    }

    .editor-wrapper {
      position: relative;
      display: flex;
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 4px;
    }

    .dark-theme .editor-wrapper {
      background: #1e1e1e;
      border-color: #444;
    }

    .line-numbers {
      padding: 8px 8px 8px 0;
      background: #f5f5f5;
      border-right: 1px solid #ddd;
      user-select: none;
      text-align: right;
      min-width: 40px;
    }

    .dark-theme .line-numbers {
      background: #252525;
      border-color: #444;
      color: #858585;
    }

    .line-number {
      font-family: monospace;
      font-size: 14px;
      line-height: 1.5;
      color: #999;
    }

    textarea {
      flex: 1;
      min-height: 200px;
      padding: 8px;
      border: none;
      border-radius: 0 4px 4px 0;
      font-family: monospace;
      font-size: 14px;
      line-height: 1.5;
      resize: vertical;
      outline: none;
      overflow-y: scroll;
    }

    .dark-theme textarea {
      background: #1e1e1e;
      color: #d4d4d4;
    }

    textarea.error {
      border-color: #ff4444;
    }

    .error-message {
      color: #ff4444;
      margin-top: 8px;
      font-size: 14px;
    }

    .dark-theme .error-message {
      color: #ff6b6b;
    }
  `]
})
/**
 * 一個 Angular JSON 編輯器組件，為 JSON 資料提供編輯、格式化和驗證功能。
 *
 * @remarks
 * - 支援深色與淺色兩種主題。
 * - 編輯器旁會顯示行號。
 * - 當 JSON 內容或主題變更時會發出事件。
 * - 提供格式化、壓縮和複製 JSON 內容的方法。
 * - 處理並顯示 JSON 解析錯誤。
 *
 * @example
 * ```html
 * <app-json-editor
 *   [initialValue]="jsonData"
 *   [darkTheme]="true"
 *   (valueChange)="onValueChange($event)"
 *   (jsonChange)="onJsonChange($event)"
 *   (themeChange)="onThemeChange($event)">
 * </app-json-editor>
 * ```
 *
 * @inputs
 * - initialValue: any — 顯示在編輯器中的初始 JSON 值。
 * - darkTheme: boolean — 是否使用深色主題。
 *
 * @outputs
 * - valueChange: EventEmitter<string> — 當內容變更時，發出原始的 JSON 字串。
 * - jsonChange: EventEmitter<any> — 當內容為有效的 JSON 時，發出解析後的 JSON 物件。
 * - themeChange: EventEmitter<boolean> — 當主題切換時，發出目前的主題狀態。
 *
 * @publicApi
 */
export class JsonEditorComponent {
  private fb = inject(FormBuilder);

  //editorForm!: FormGroup;
  hasError = false;
  errorMessage = '';
  isDarkTheme = false;
  lineNumbersArray: number[] = [];
  content: any;
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set initialValue(value: any) {
    if (value) {
      /*this.editorForm.patchValue({
        content: typeof value === 'string' ? value : JSON.stringify(value, null, 2)
      });*/
      this.content = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
      this.updateLineNumbers();
    }
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set darkTheme(value: boolean) {
    this.isDarkTheme = value;
  }

  readonly valueChange = output<string>();
  readonly jsonChange = output<any>();
  readonly themeChange = output<boolean>();




  constructor() {
    /*this.editorForm = this.fb.group({
      content: ['']
    });*/
    this.content = '';
    this.updateLineNumbers();
  }


  updateLineNumbers() {
    const content = this.content; // this.editorForm.get('content').value;
    const lineCount = content ? content.split('\n').length : 1;
    this.lineNumbersArray = Array.from({ length: lineCount }, (_, i) => i + 1);
  }

  onContentChange(event: Event) {
    const content = (event.target as HTMLTextAreaElement).value;
    this.valueChange.emit(content);
    this.updateLineNumbers();

    try {
      if (content.trim()) {
        const parsedJson = JSON.parse(content);
        this.jsonChange.emit(parsedJson);
        this.hasError = false;
        this.errorMessage = '';
      }
    } catch (e: any) {
      this.hasError = true;
      this.errorMessage = `JSON解析錯誤: ${e.message}`;
    }
  }

  syncScroll(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    const lineNumbers = textarea.previousElementSibling as HTMLElement;
    lineNumbers.scrollTop = textarea.scrollTop;
  }

  format() {
    try {
      //const content = this.editorForm.get('content').value;
      const formatted = JSON.stringify(JSON.parse(this.content), null, 2);
      //this.editorForm.patchValue({ content: formatted });
      this.content = formatted;
      this.updateLineNumbers();
    } catch (e: any) {
      this.hasError = true;
      this.errorMessage = `格式化失敗: ${e.message}`;
    }
  }

  compact() {
    try {
      //const content = this.editorForm.get('content').value;
      const compacted = JSON.stringify(JSON.parse(this.content));
      //this.editorForm.patchValue({ content: compacted });
      this.content = compacted;
      this.updateLineNumbers();
    } catch (e: any) {
      this.hasError = true;
      this.errorMessage = `壓縮失敗: ${e.message}`;
    }
  }

  async copyToClipboard() {
    try {
      //const content = this.editorForm.get('content').value;
      await navigator.clipboard.writeText(this.content);
    } catch (e) {
      console.error('複製到剪貼簿失敗:', e);
    }
  }

  toggleTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    this.themeChange.emit(this.isDarkTheme);
  }
}
